import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send } from "lucide-react";

const API = "/api"; // same-origin — the ingress/worker proxies /api to the backend

const SUGGESTIONS = [
  "What are the three pillars?",
  "How do I become a member?",
  "What is the Bourg Merchant Exchange?",
];

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const submit = async (e, preset) => {
    if (e) e.preventDefault();
    const message = (preset || text).trim();
    if (!message || busy) return;
    const next = [...messages, { role: "user", content: message }];
    setMessages(next);
    setText("");
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, history: messages.slice(-20) }),
      });
      if (!res.ok) throw new Error("request failed");
      const data = await res.json();
      setMessages([...next, { role: "assistant", content: data.answer }]);
    } catch {
      setMessages([
        ...next,
        {
          role: "assistant",
          content:
            "The assistant is temporarily unavailable. Please reach the council directly via WhatsApp.",
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="chat-widget" className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3">
      {open && (
        <div
          data-testid="chat-panel"
          className="w-[calc(100vw-2.5rem)] max-w-sm rounded-2xl border border-stone-700/80 bg-stone-950/95 backdrop-blur shadow-2xl overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-stone-800 bg-stone-900/80">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-500 pulse-glow"></span>
              <div>
                <div className="text-sm font-bold text-stone-100 display-serif">
                  Ask the Institute
                </div>
                <div className="mono-tag text-[10px] uppercase tracking-wider text-stone-400">
                  Private · No web research
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="chat-close-button"
              aria-label="Close chat"
              className="text-stone-400 hover:text-amber-400 transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          <div ref={scrollRef} className="h-80 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <div className="space-y-3">
                <p className="text-xs text-stone-400 leading-relaxed">
                  Ask about our mission, membership, the three pillars, or the Bourg Merchant
                  Exchange. Answers come only from the Institute's approved material.
                </p>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => submit(null, s)}
                      data-testid={`chat-suggestion-${SUGGESTIONS.indexOf(s)}`}
                      className="text-[11px] px-3 py-1.5 rounded-full border border-stone-700 text-stone-300 hover:border-amber-600/60 hover:text-amber-300 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "text-right" : "text-left"}>
                <span
                  data-testid={`chat-message-${m.role}-${i}`}
                  className={`inline-block max-w-[90%] whitespace-pre-wrap rounded-xl px-3 py-2 text-xs leading-relaxed ${
                    m.role === "user"
                      ? "bg-amber-600 text-stone-950 font-medium"
                      : "bg-stone-900 border border-stone-800 text-stone-200"
                  }`}
                >
                  {m.content}
                </span>
              </div>
            ))}
            {busy && (
              <div data-testid="chat-typing-indicator" className="text-left">
                <span className="inline-block rounded-xl px-3 py-2 text-xs bg-stone-900 border border-stone-800 text-stone-400">
                  Consulting the council…
                </span>
              </div>
            )}
          </div>

          <form onSubmit={submit} className="flex gap-2 p-3 border-t border-stone-800 bg-stone-900/60">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={4000}
              placeholder="Ask a question…"
              aria-label="Question"
              data-testid="chat-input"
              className="flex-1 min-w-0 rounded-lg bg-stone-950 border border-stone-700 px-3 py-2 text-xs text-stone-100 placeholder:text-stone-500 focus:outline-none focus:border-amber-600/60"
            />
            <button
              type="submit"
              disabled={busy || !text.trim()}
              data-testid="chat-send-button"
              aria-label="Send message"
              className="shrink-0 w-9 h-9 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:hover:bg-amber-500 text-stone-950 flex items-center justify-center transition-colors"
            >
              <Send size={15} />
            </button>
          </form>
        </div>
      )}

      <button
        onClick={() => setOpen(!open)}
        data-testid="chat-toggle-button"
        aria-label={open ? "Close assistant" : "Open assistant"}
        className="w-14 h-14 rounded-full bg-amber-500 hover:bg-amber-400 text-stone-950 flex items-center justify-center shadow-[0_0_24px_rgba(217,119,6,0.4)] transition-colors"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>
    </div>
  );
};

export default ChatWidget;
